import { memo, useCallback, useEffect, useMemo, useState } from "react";
import type { InspectionState } from "@/views/inspections/components/inspections-lists/InspectionsLists.d";
import { useTranslate } from "@/hooks/use-translate/useTranslate";
import SecondaryButton from "@/components/buttons/secondary-button/SecondaryButton";
import { getFieldsToSearch } from "../utils/inspections.utils";
import InputText from "@/components/input-text/InputText";
import InputSelect from "@/components/input-select/InputSelect";
import InputCalendar from "@/components/input-calendar/InputCalendar";
import { ENGINE_COLOURS } from "@/assets/constants/selectsLists";

interface InspectionsSearchersProps {
  tableType: InspectionState;
  rendered: boolean;
  searchInspections: (params: string) => void;
  lastFiltered: string;
  cleanLastSort: () => void;
}

const InspectionsSearchers = ({
  tableType = "active",
  rendered = false,
  lastFiltered,
  cleanLastSort,
  searchInspections,
}: InspectionsSearchersProps) => {
  const translate = useTranslate();

  const [snStand, setSnStand] = useState("");
  const [esnEnter, setEsnEnter] = useState("");
  const [esnExit, setEsnExit] = useState("");
  const [engineId, setEngineId] = useState("");
  const [colour, setColour] = useState("");
  const [colourToSearch, setColourToSearch] = useState("");
  const [phase, setPhase] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const fieldsToSearch = useMemo(
    () => getFieldsToSearch(tableType, true),
    [tableType],
  );

  useEffect(() => {
    if (!rendered || lastFiltered.length === 0) {
      setSnStand("");
      setEsnEnter("");
      setEsnExit("");
      setEngineId("");
      setColour("");
      setColourToSearch("");
      setPhase("");
      setStartDate("");
      setEndDate("");
    }
  }, [rendered, lastFiltered.length]);

  useEffect(() => {
    if (colour.length) {
      const newColour = ENGINE_COLOURS.find(
        (colourElement) => colourElement.value === colour,
      )?.label as string;
      setColourToSearch(newColour);
    } else {
      setColourToSearch("");
    }
  }, [colour]);

  const createSearcher = useCallback(
    (
      type: string,
      name: string,
      options: { label: string; value: string }[],
    ) => {
      let value = "";
      switch (name) {
        case "snStand":
          value = snStand;
          break;
        case "esnEnter":
          value = esnEnter;
          break;
        case "esnExit":
          value = esnExit;
          break;
        case "engineType":
          value = engineId;
          break;
        case "colour":
          value = colour;
          break;
        case "phase":
          value = phase;
          break;
        case "startDate":
          value = startDate;
          break;
        case "endDate":
          value = endDate;
          break;
        default:
          break;
      }

      switch (type) {
        case "string":
          return (
            <div className="w-full mb-1">
              <InputText
                value={value}
                name={name}
                placeholder={translate(name)}
                onChange={(data) => {
                  switch (name) {
                    case "snStand":
                      setSnStand(data);
                      break;
                    case "esnEnter":
                      setEsnEnter(data);
                      break;
                    case "esnExit":
                      setEsnExit(data);
                      break;
                  }
                }}
                size="medium"
                noBottomSpace
                icon="searchLens"
              />
            </div>
          );
        case "select":
          return (
            <div className="w-full mb-1">
              <InputSelect
                value={value}
                onChange={(data) => {
                  switch (name) {
                    case "engineType":
                      setEngineId(data);
                      break;
                    case "colour":
                      setColour(data);
                      break;
                    case "phase":
                      setPhase(data);
                      break;
                    default:
                      break;
                  }
                }}
                placeholder={translate(name)}
                options={options}
                id={name}
              />
            </div>
          );
        case "date":
          return (
            <div className="w-full mb-1">
              <InputCalendar
                label={name}
                size="medium"
                name={name}
                noBottomSpace
                value={value ? new Date(value) : null}
                onChange={(data) => {
                  if (name === "startDate") {
                    setStartDate(data ? data.toISOString() : "");
                  } else {
                    setEndDate(data ? data.toISOString() : "");
                  }
                }}
              />
            </div>
          );
        default:
          return name;
      }
    },
    [
      snStand,
      esnEnter,
      esnExit,
      engineId,
      colour,
      phase,
      startDate,
      endDate,
      translate,
    ],
  );

  const disabledButton = useMemo((): boolean => {
    const noFiltersSelected =
      !snStand &&
      !esnEnter &&
      !esnExit &&
      !engineId &&
      !colour &&
      !phase &&
      !startDate &&
      !endDate;
    return noFiltersSelected;
  }, [snStand, esnEnter, esnExit, engineId, colour, phase, startDate, endDate]);

  const search = useCallback(() => {
    const searchParams = [
      `?status=${tableType === "active" ? "IN_PROGRESS" : "CLOSED"}&size=10`,
    ];
    if (startDate) searchParams.push(`createdAt=${startDate.substring(0, 10)}`);
    if (endDate) searchParams.push(`endDate=${endDate.substring(0, 10)}`);
    if (snStand) searchParams.push(`nsStand=${snStand}`);
    if (esnEnter) searchParams.push(`esnIn=${esnEnter}`);
    if (esnExit) searchParams.push(`esnOut=${esnExit}`);
    if (phase) searchParams.push(`phaseId=${phase}`);
    if (engineId) searchParams.push(`engineId=${engineId}`);
    if (colourToSearch) searchParams.push(`colour=${colourToSearch}`);
    searchParams.push("page=");
    const params = searchParams.join("&").replaceAll(" ", "%20");
    searchInspections(params);
    cleanLastSort();
  }, [
    snStand,
    esnEnter,
    esnExit,
    phase,
    engineId,
    colourToSearch,
    tableType,
    searchInspections,
    startDate,
    endDate,
    cleanLastSort,
  ]);

  return (
    <>
      {rendered ? (
        <div className="w-full flex flex-col gap-y-2">
          {fieldsToSearch.map((field, index) => (
            <div key={`th-searcher-${index}`} className="w-full">
              <>
                {createSearcher(
                  field.type,
                  field.name,
                  field.data as { label: string; value: string }[],
                )}
              </>
            </div>
          ))}
          <div className="w-full pb-2" data-testid="search-button">
            <SecondaryButton
              label={translate("search")}
              colour="red"
              onClick={search}
              disabled={disabledButton}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default memo(InspectionsSearchers);
